import {
  createElement,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react"
import {
  ButtonIcon,
  ChevronLeft,
  HeadingXSmall,
  ModalHeader,
} from "@mezo-org/mezo-clay"

import BaseModal, { BaseModalProps } from "../BaseModal"

import StepIndicator from "./StepIndicator"
import StepperModalProvider, {
  StepperModalValues,
  useStepperModalContext,
} from "./StepperModalContext"

export type ComponentStepProps = {
  onClickContinue: () => void
  onClickBack: () => void
  onClose: () => void
}

type StepProps = {
  title?: ReactNode
  withCloseButton?: boolean
  withBackButton?: boolean
  component: (props: ComponentStepProps) => ReactElement | null
  withStepIndicator?: boolean
}

type StepperModalProps = {
  steps: StepProps[]
  numberOfStepsInStepper?: number
} & BaseModalProps

type StepperModalWrapperProps = StepperModalProps & {
  initialContextValue?: StepperModalValues
}

function StepperModal({
  steps,
  onClose,
  numberOfStepsInStepper,
  variant,
  ...props
}: StepperModalProps) {
  const [stepIndex, setStepIndex] = useState(0)
  const { resetState } = useStepperModalContext()
  const step = useMemo(() => steps[stepIndex], [stepIndex, steps])

  const stepCount = steps.length

  const onClickClose = useCallback(() => {
    setStepIndex(0)
    resetState()
    onClose?.({})
  }, [onClose, resetState])

  const onClickContinue = useCallback(() => {
    if (stepIndex === stepCount - 1) {
      onClickClose()
    } else {
      setStepIndex(stepIndex + 1)
    }
  }, [stepIndex, stepCount, onClickClose])

  const onClickBack = useCallback(() => {
    if (stepIndex === 0) onClickClose()
    else setStepIndex(stepIndex - 1)
  }, [stepIndex, onClickClose])

  const shouldRenderBackButton = step.withBackButton ?? true
  const stepperStepsCount = numberOfStepsInStepper ?? stepCount
  const shouldDisplayStepIndicator = step.withStepIndicator ?? false

  return (
    <BaseModal
      closeable={!!step.withCloseButton}
      onClose={onClickClose}
      variant={variant}
      {...props}
    >
      {step.title && (
        <ModalHeader>
          {stepIndex > 0 && shouldRenderBackButton && (
            <ButtonIcon
              shape="circle"
              size="small"
              onClick={onClickBack}
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    position: "absolute",
                    top: $theme.sizing.scale550,
                    left: $theme.sizing.scale550,
                  }),
                },
              }}
            >
              <ChevronLeft size={16} />
            </ButtonIcon>
          )}
          <HeadingXSmall style={{ textAlign: "center" }}>
            {step.title}
          </HeadingXSmall>
          {shouldDisplayStepIndicator && (
            <StepIndicator
              numberOfSteps={stepperStepsCount}
              currentStep={stepIndex}
            />
          )}
        </ModalHeader>
      )}
      {createElement(step.component, {
        onClickContinue,
        onClickBack,
        onClose: onClickClose,
      })}
    </BaseModal>
  )
}

export default function StepperModalWrapper({
  initialContextValue,
  ...props
}: StepperModalWrapperProps) {
  return (
    <StepperModalProvider initialValues={initialContextValue}>
      <StepperModal {...props} />
    </StepperModalProvider>
  )
}
